import {
  forwardRef,
  type Ref,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import useNurses from "@hooks/useNurses";
import type { NurseResponse, NurseWithRelation } from "@vTypes/nurse-type";
import { linkPreceptorRelations } from "@utils/nurse-util";
import CustomModal from "@/components/modal/CustomModal";
import { MSG } from "@constants/MSG.ts";
import { toast } from "@/components/toast/CustomToast";
import {
  useTeamsQuery,
  type TeamsSchema,
} from "@/hooks/roster-craete/useTeamsQuery";
import { QUERY_BASE_KEY } from "@constants/query-key";
import httpClient from "@/config/axios";
import ActionButton from "@/components/ui/button/btn-action";
import { TeamTransferTables } from "@/pages/roster-management/components/TeamTransferTables";
import TeamManagementModal from "./TeamManagementModal";
import TeamShiftConfigModal, {
  type TeamShiftCounts,
} from "./TeamShiftConfigModal";
import GridModalHeader from "@/components/ui/modal/grid-modal-header";
import GridModalTitle from "@/components/ui/modal/grid-modal-title";
import { images } from "@/assets/img/imagesMap";
import { useShiftsQuery } from "@/hooks/roster-craete/useShiftsQuery";
import useRosterConfig from "@/hooks/useRosterConfig";
import type { GradeShiftGridRowDisplay } from "./GradeShiftConfigModal";
import type { ShiftType } from "@utils/grade-util";
import { validateShiftCoverage } from "@utils/shift-coverage-validation";

export type TeamSettingContentsHandle = {
  triggerSave: () => void;
  triggerSaveDirect: () => void;
};

type TeamSettingContentsProps = {
  onClose: () => void;
  selectedGroupId: string;
  onMutationPendingChange?: (pending: boolean) => void;
  onDirtyChange?: (dirty: boolean) => void;
};

function TeamSettingContents(
  {
    onClose,
    selectedGroupId,
    onMutationPendingChange,
    onDirtyChange,
  }: TeamSettingContentsProps,
  ref: Ref<TeamSettingContentsHandle>
) {
  const [isSaveConfirmOpen, setSaveConfirmOpen] = useState(false);
  const [isManageOpen, setManageOpen] = useState(false);
  const [isShiftConfigOpen, setShiftConfigOpen] = useState(false);
  /** Team 관리 모달 전용 초안. 모달 저장 시에만 로컬 상태에 반영한다. */
  const [manageDraftTeams, setManageDraftTeams] = useState<TeamsSchema | null>(
    null
  );
  /** Strict Mode에서 중복 상태 갱신을 피하기 위한 최신 초안 참조 */
  const manageDraftTeamsRef = useRef<TeamsSchema | null>(null);
  manageDraftTeamsRef.current = manageDraftTeams;

  const queryClient = useQueryClient();

  const groupId = selectedGroupId ?? "";
  const { data: teamsData } = useTeamsQuery(groupId);
  const { data: nurseData } = useNurses(groupId);
  const { data: shiftsData } = useShiftsQuery(groupId);
  const { data: rosterConfig } = useRosterConfig();

  const [localTeams, setLocalTeams] = useState<TeamsSchema>([]);
  const [nursesByTeam, setNursesByTeam] = useState<
    Record<number, NurseWithRelation[]>
  >({});
  const [unassignedNurses, setUnassignedNurses] = useState<NurseWithRelation[]>(
    []
  );
  const [deletedTeamIds, setDeletedTeamIds] = useState<number[]>([]);
  const [isDirty, setIsDirty] = useState(false);
  const [teamShiftDraftTeams, setTeamShiftDraftTeams] =
    useState<TeamsSchema | null>(null);
  const [teamShiftDraftSettings, setTeamShiftDraftSettings] = useState<Record<
    number,
    TeamShiftCounts
  > | null>(null);

  const [leftSelectedTeamId, setLeftSelectedTeamId] = useState<
    number | "unassigned" | null
  >("unassigned");
  const [rightSelectedTeamId, setRightSelectedTeamId] = useState<
    number | "unassigned" | null
  >(null);
  const [highlightedNurseId, setHighlightedNurseId] = useState<string | null>(
    null
  );

  const [originalNursesByTeam, setOriginalNursesByTeam] = useState<
    Record<number, string[]>
  >({});

  const isUseMid = rosterConfig?.use_mid !== false;

  const constraintLetters = useMemo(
    (): readonly ShiftType[] =>
      isUseMid ? ["D", "E", "N", "M"] : ["D", "E", "N"],
    [isUseMid]
  );

  const shiftDisplayInfo = useMemo((): GradeShiftGridRowDisplay[] => {
    const fallbackColor: Partial<Record<ShiftType, string>> = {
      D: "#59DDB7",
      E: "#72BFFF",
      N: "#BAB0F0",
      M: "#C4B5FD",
    };
    const labelByDefault = new Map<string, string>();
    const colorByDefault = new Map<string, string>();

    (shiftsData ?? []).forEach((s) => {
      const key = s.default_shift;
      if (!key || labelByDefault.has(key)) return;

      const shiftId = typeof s.shift_id === "string" ? s.shift_id.trim() : "";
      const name = typeof s.name === "string" ? s.name.trim() : "";
      labelByDefault.set(key, shiftId || name || key);
      colorByDefault.set(key, s.color ?? "");
    });

    return constraintLetters.map((letter) => ({
      constraintKey: letter,
      displayId: labelByDefault.get(letter) ?? letter,
      color: colorByDefault.get(letter) || fallbackColor[letter] || "#9CA3AF",
    }));
  }, [constraintLetters, shiftsData]);

  useEffect(() => {
    if (teamsData && nurseData) {
      setLocalTeams(teamsData);
      setDeletedTeamIds([]);
      setIsDirty(false);

      const map: Record<number, NurseWithRelation[]> = {};
      const unassigned: NurseWithRelation[] = [];
      const originalMap: Record<number, string[]> = {};

      teamsData.forEach((t) => {
        map[t.team_id] = [];
        originalMap[t.team_id] = [];
      });

      const withRelations = linkPreceptorRelations(nurseData);

      withRelations.forEach((n) => {
        if (!n.active) return;

        if (n.team_id && map[n.team_id]) {
          map[n.team_id].push(n);
          originalMap[n.team_id].push(n.nurse_id);
        } else {
          unassigned.push(n);
        }
      });

      setNursesByTeam(map);
      setUnassignedNurses(unassigned);
      setOriginalNursesByTeam(originalMap);
    }
  }, [teamsData, nurseData]);

  /**
   * Team 목록과 선택 상태 동기화:
   * - Team이 없으면 오른쪽 선택을 비운다.
   * - 기존 선택이 아직 존재하면 유지하고, 아니면 첫 Team으로 이동한다.
   */
  useEffect(() => {
    if (localTeams.length === 0) {
      setRightSelectedTeamId(null);
      return;
    }
    setRightSelectedTeamId((prev) => {
      if (prev === "unassigned") return "unassigned";
      if (
        typeof prev === "number" &&
        localTeams.some((t) => t.team_id === prev)
      ) {
        return prev;
      }
      return localTeams[0].team_id;
    });
  }, [localTeams]);

  const { mutateAsync, isPending } = useMutation({
    mutationFn: async () => {
      const teamsPayload: {
        team_id?: number;
        team_name: string;
        add: string[];
        remove: string[];
        min_shift?: TeamShiftCounts | null;
        handoff_policy?: Record<string, unknown> | null;
      }[] = [];

      const existingTeams = localTeams.filter((t) => t.team_id > 0);
      existingTeams.forEach((t) => {
        const currentMembers = (nursesByTeam[t.team_id] || []).map(
          (n) => n.nurse_id
        );
        const originalMembers = originalNursesByTeam[t.team_id] || [];

        const add = currentMembers.filter(
          (id) => !originalMembers.includes(id)
        );
        const remove = originalMembers.filter(
          (id) => !currentMembers.includes(id)
        );

        teamsPayload.push({
          team_id: t.team_id,
          team_name: t.team_name,
          add,
          remove,
          min_shift: t.min_shift ?? null,
          handoff_policy: t.handoff_policy ?? null,
        });
      });

      const newTeams = localTeams.filter((t) => t.team_id < 0);
      newTeams.forEach((t) => {
        const members = (nursesByTeam[t.team_id] || []).map((n) => n.nurse_id);
        teamsPayload.push({
          team_name: t.team_name,
          add: members,
          remove: [],
          min_shift: t.min_shift ?? null,
          handoff_policy: t.handoff_policy ?? null,
        });
      });

      const teamsUrl = groupId
        ? `/teams?group_id=${encodeURIComponent(groupId)}`
        : "/teams";

      await httpClient.put(teamsUrl, {
        teams: teamsPayload,
        delete_team_ids: deletedTeamIds,
      });
    },
    onSuccess: () => {
      const nurseTeamMap = new Map<string, number | null>();
      Object.entries(nursesByTeam).forEach(([teamId, nurses]) => {
        nurses.forEach((nurse) => {
          nurseTeamMap.set(nurse.nurse_id, Number(teamId));
        });
      });
      unassignedNurses.forEach((nurse) => {
        nurseTeamMap.set(nurse.nurse_id, null);
      });

      queryClient.setQueryData<TeamsSchema>(
        [QUERY_BASE_KEY.TEAMS, groupId],
        (prev) =>
          prev?.map((team) => ({
            ...team,
            team_members:
              nursesByTeam[team.team_id]?.map((nurse) => nurse.nurse_id) ?? [],
          }))
      );
      queryClient.setQueryData<NurseResponse[]>(
        [QUERY_BASE_KEY.NURSES, groupId],
        (prev) =>
          prev?.map((nurse) =>
            nurseTeamMap.has(nurse.nurse_id)
              ? { ...nurse, team_id: nurseTeamMap.get(nurse.nurse_id) ?? null }
              : nurse
          )
      );
      queryClient.setQueriesData<{ nurse_id: string; team_id: string | null }>(
        { queryKey: [QUERY_BASE_KEY.NURSES, "detail"] },
        (prev) => {
          if (!prev || !nurseTeamMap.has(prev.nurse_id)) return prev;
          const teamId = nurseTeamMap.get(prev.nurse_id);
          return { ...prev, team_id: teamId == null ? null : String(teamId) };
        }
      );

      toast.success("Team 설정이 저장되었습니다.");
      setSaveConfirmOpen(false);
      setIsDirty(false);
      onClose();
      void Promise.all([
        queryClient.invalidateQueries({
          queryKey: [QUERY_BASE_KEY.TEAMS],
          refetchType: "all",
        }),
        queryClient.invalidateQueries({
          queryKey: [QUERY_BASE_KEY.NURSES, groupId],
          refetchType: "all",
        }),
        queryClient.invalidateQueries({
          queryKey: [QUERY_BASE_KEY.NURSES, "detail"],
          refetchType: "all",
        }),
      ]);
    },
    onError: (error) => {
      console.error(error);
      toast.error("저장에 실패했습니다.");
    },
  });

  useEffect(() => {
    onMutationPendingChange?.(isPending);
  }, [isPending, onMutationPendingChange]);

  useEffect(() => {
    onDirtyChange?.(isDirty);
  }, [isDirty, onDirtyChange]);

  const currentTeamCoverageValidation = useMemo(
    () =>
      validateShiftCoverage({
        buckets: localTeams.map((team) => ({
          id: team.team_id,
          label: team.team_name,
        })),
        shiftSettings: Object.fromEntries(
          localTeams.map((team) => [team.team_id, team.min_shift ?? {}])
        ) as Partial<Record<number, TeamShiftCounts>>,
        nursesByBucket: nursesByTeam,
        shiftKeys: constraintLetters,
      }),
    [constraintLetters, localTeams, nursesByTeam]
  );

  const validateBeforeSave = useCallback(() => {
    const errors = currentTeamCoverageValidation.messages;
    if (errors.length > 0) {
      return confirm(
        `근무 가능 인원 위반사항이 있습니다.\n\n${errors.join(
          "\n"
        )}\n\n그래도 저장하시겠습니까?`
      );
    }
    return true;
  }, [currentTeamCoverageValidation.messages]);

  const triggerSave = useCallback(() => {
    if (!validateBeforeSave()) return;
    setSaveConfirmOpen(true);
  }, [validateBeforeSave]);

  const triggerSaveDirect = useCallback(() => {
    if (isPending) return;
    if (!validateBeforeSave()) return;
    void mutateAsync();
  }, [isPending, mutateAsync, validateBeforeSave]);

  useImperativeHandle(
    ref,
    () => ({
      triggerSave,
      triggerSaveDirect,
    }),
    [triggerSave, triggerSaveDirect]
  );

  const getNextTempTeamIdFromDraft = useCallback((draft: TeamsSchema) => {
    let minId = 0;
    for (const team of draft) {
      if (team.team_id < minId) minId = team.team_id;
    }
    return minId - 1;
  }, []);

  const openManageModal = useCallback(() => {
    setManageDraftTeams(
      localTeams.map((t) => ({
        ...t,
        team_members: [...t.team_members],
      }))
    );
    setManageOpen(true);
  }, [localTeams]);

  const closeManageModal = useCallback(() => {
    setManageOpen(false);
    setManageDraftTeams(null);
  }, []);

  const buildTeamShiftSettings = useCallback(
    (teams: TeamsSchema): Record<number, TeamShiftCounts> => {
      const base = Object.fromEntries(
        constraintLetters.map((key) => [key, 0])
      ) as TeamShiftCounts;

      return teams.reduce<Record<number, TeamShiftCounts>>((acc, team) => {
        acc[team.team_id] = {
          ...base,
          ...(team.min_shift ?? {}),
        };
        return acc;
      }, {});
    },
    [constraintLetters]
  );

  const openShiftConfigModal = useCallback(() => {
    const draftTeams = localTeams.map((team) => ({
      ...team,
      team_members: [...team.team_members],
      min_shift: team.min_shift ? { ...team.min_shift } : null,
      handoff_policy: team.handoff_policy ? { ...team.handoff_policy } : null,
    }));
    setTeamShiftDraftTeams(draftTeams);
    setTeamShiftDraftSettings(buildTeamShiftSettings(draftTeams));
    setShiftConfigOpen(true);
  }, [buildTeamShiftSettings, localTeams]);

  const closeShiftConfigModal = useCallback(() => {
    setShiftConfigOpen(false);
    setTeamShiftDraftTeams(null);
    setTeamShiftDraftSettings(null);
  }, []);

  const handleManageDraftAdd = useCallback(
    (name: string): number | undefined => {
      const trimmed = name.trim();
      if (!trimmed) return undefined;

      const prev = manageDraftTeamsRef.current;
      if (!prev) return undefined;
      if (prev.some((t) => t.team_name.trim() === trimmed)) {
        toast.error("이미 존재하는 Team 이름입니다.");
        return undefined;
      }

      const tempId = getNextTempTeamIdFromDraft(prev);
      setManageDraftTeams([
        ...prev,
        { team_id: tempId, team_name: trimmed, team_members: [] },
      ]);
      return tempId;
    },
    [getNextTempTeamIdFromDraft]
  );

  const handleManageDraftRename = useCallback((id: number, name: string) => {
    const trimmed = name.trim();
    if (!trimmed) return;

    const prev = manageDraftTeamsRef.current;
    if (!prev) return;
    if (prev.some((t) => t.team_id !== id && t.team_name.trim() === trimmed)) {
      toast.error("이미 존재하는 Team 이름입니다.");
      return;
    }

    setManageDraftTeams(
      prev.map((t) => (t.team_id === id ? { ...t, team_name: trimmed } : t))
    );
  }, []);

  const handleManageDraftDelete = useCallback((id: number) => {
    setManageDraftTeams((prev) => {
      if (!prev) return prev;
      return prev.filter((t) => t.team_id !== id);
    });
  }, []);

  const handleTeamShiftDraftAdd = useCallback(() => {
    const raw = prompt("새 Team 이름을 입력하세요.");
    const name = raw?.trim();
    if (!name) return;

    setTeamShiftDraftTeams((prev) => {
      if (!prev) return prev;
      if (prev.some((t) => t.team_name.trim() === name)) {
        toast.error("중복된 Team 이름이 있습니다.");
        return prev;
      }

      const tempId = getNextTempTeamIdFromDraft(prev);
      setTeamShiftDraftSettings((settings) => ({
        ...(settings ?? {}),
        [tempId]: Object.fromEntries(
          constraintLetters.map((key) => [key, 0])
        ) as TeamShiftCounts,
      }));
      return [
        ...prev,
        {
          team_id: tempId,
          team_name: name,
          team_members: [],
          min_shift: null,
          handoff_policy: null,
        },
      ];
    });
  }, [constraintLetters, getNextTempTeamIdFromDraft]);

  const handleTeamShiftDraftRename = useCallback(
    (id: number, currentName: string) => {
      const raw = prompt("Team 이름을 입력하세요.", currentName);
      const name = raw?.trim();
      if (!name) return;

      setTeamShiftDraftTeams((prev) => {
        if (!prev) return prev;
        if (prev.some((t) => t.team_id !== id && t.team_name.trim() === name)) {
          toast.error("중복된 Team 이름이 있습니다.");
          return prev;
        }
        return prev.map((t) =>
          t.team_id === id ? { ...t, team_name: name } : t
        );
      });
    },
    []
  );
  const handleTeamShiftDraftDelete = useCallback((id: number) => {
    setTeamShiftDraftTeams((prev) => {
      if (!prev) return prev;
      return prev.filter((t) => t.team_id !== id);
    });
    setTeamShiftDraftSettings((prev) => {
      if (!prev) return prev;
      const next = { ...prev };
      delete next[id];
      return next;
    });
  }, []);

  const handleTeamShiftMinStaffChange = useCallback(
    (teamId: number, shift: ShiftType, value: string) => {
      const nextValue = value === "" ? 0 : Number(value);
      setTeamShiftDraftSettings((prev) => ({
        ...(prev ?? {}),
        [teamId]: {
          ...(prev?.[teamId] ?? {}),
          [shift]: Number.isFinite(nextValue) ? nextValue : 0,
        },
      }));
    },
    []
  );

  const teamShiftModalTeams = teamShiftDraftTeams ?? [];
  const teamShiftModalSettings = teamShiftDraftSettings ?? {};

  const teamShiftModalCoverageValidation = useMemo(
    () =>
      validateShiftCoverage({
        buckets: teamShiftModalTeams.map((team) => ({
          id: team.team_id,
          label: team.team_name,
        })),
        shiftSettings: teamShiftModalSettings,
        nursesByBucket: nursesByTeam,
        shiftKeys: constraintLetters,
      }),
    [
      constraintLetters,
      nursesByTeam,
      teamShiftModalSettings,
      teamShiftModalTeams,
    ]
  );

  const confirmManageModal = useCallback(() => {
    if (!manageDraftTeams) return;

    const next = manageDraftTeams.map((t) => ({
      ...t,
      team_name: t.team_name.trim(),
    }));

    const names = next.map((t) => t.team_name);
    if (names.some((n) => !n)) {
      toast.error("Team 이름을 입력해주세요.");
      return;
    }
    if (new Set(names).size !== names.length) {
      toast.error("중복된 Team 이름이 있습니다.");
      return;
    }

    const prevTeamIds = new Set(localTeams.map((t) => t.team_id));
    const nextTeamIds = new Set(next.map((t) => t.team_id));
    const removedIds = [...prevTeamIds].filter((id) => !nextTeamIds.has(id));

    const removedWithMembers = removedIds.filter(
      (id) => (nursesByTeam[id]?.length ?? 0) > 0
    );
    if (removedWithMembers.length > 0) {
      const lines = removedWithMembers
        .map((id) => {
          const nm = localTeams.find((t) => t.team_id === id)?.team_name ?? "";
          const c = nursesByTeam[id]?.length ?? 0;
          return `- ${nm}: ${c}명`;
        })
        .join("\n");
      const ok = confirm(
        `삭제되는 Team의 소속 근무자는 미지정으로 이동합니다.\n계속할까요?\n\n${lines}`
      );
      if (!ok) return;
    }

    setUnassignedNurses((prevUnassigned) => {
      let u = prevUnassigned;
      for (const id of removedIds) {
        const members = nursesByTeam[id] || [];
        if (members.length === 0) continue;
        u = [...u, ...members.map((n) => ({ ...n, team_id: null }))];
      }
      return u;
    });

    setNursesByTeam((prevMap) => {
      const copy = { ...prevMap };
      for (const id of removedIds) {
        delete copy[id];
      }
      for (const t of next) {
        if (copy[t.team_id] === undefined) {
          copy[t.team_id] = [];
        }
      }
      return copy;
    });

    setDeletedTeamIds((prevDeleted) => {
      let out = prevDeleted;
      for (const id of removedIds) {
        if (id > 0 && !out.includes(id)) out = [...out, id];
      }
      return out;
    });

    setLocalTeams(next);

    const liftRightToFirstNewTeam = localTeams.length === 0 && next.length > 0;

    setRightSelectedTeamId((prev) => {
      if (liftRightToFirstNewTeam) return next[0].team_id;
      if (typeof prev === "number" && removedIds.includes(prev)) {
        return next[0]?.team_id ?? null;
      }
      return prev;
    });

    setLeftSelectedTeamId((prev) => {
      if (typeof prev === "number" && removedIds.includes(prev)) {
        return "unassigned";
      }
      return prev;
    });

    setIsDirty(true);
    setManageOpen(false);
    setManageDraftTeams(null);
  }, [manageDraftTeams, localTeams, nursesByTeam]);

  const confirmShiftConfigModal = useCallback(() => {
    if (!teamShiftDraftTeams || !teamShiftDraftSettings) return;

    const coverageErrors = teamShiftModalCoverageValidation.messages;
    if (coverageErrors.length > 0) {
      const ok = confirm(
        `근무 가능 인원 위반사항이 있습니다.\n\n${coverageErrors.join(
          "\n"
        )}\n\n그래도 반영하시겠습니까?`
      );
      if (!ok) return;
    }

    const next = teamShiftDraftTeams.map((team) => {
      const minShift = constraintLetters.reduce<TeamShiftCounts>((acc, key) => {
        const value = Number(teamShiftDraftSettings[team.team_id]?.[key]) || 0;
        acc[key] = value;
        return acc;
      }, {});

      return {
        ...team,
        team_name: team.team_name.trim(),
        min_shift: minShift,
      };
    });

    const names = next.map((t) => t.team_name);
    if (names.some((name) => !name)) {
      toast.error("Team 이름을 입력해주세요.");
      return;
    }
    if (new Set(names).size !== names.length) {
      toast.error("중복된 Team 이름이 있습니다.");
      return;
    }

    const prevTeamIds = new Set(localTeams.map((t) => t.team_id));
    const nextTeamIds = new Set(next.map((t) => t.team_id));
    const removedIds = [...prevTeamIds].filter((id) => !nextTeamIds.has(id));

    const removedWithMembers = removedIds.filter(
      (id) => (nursesByTeam[id]?.length ?? 0) > 0
    );
    if (removedWithMembers.length > 0) {
      const lines = removedWithMembers
        .map((id) => {
          const name =
            localTeams.find((team) => team.team_id === id)?.team_name ?? "";
          const count = nursesByTeam[id]?.length ?? 0;
          return `- ${name}: ${count}명`;
        })
        .join("\n");
      const ok = confirm(
        `삭제되는 Team의 소속 근무자는 미지정으로 이동합니다.\n계속할까요?\n\n${lines}`
      );
      if (!ok) return;
    }

    setUnassignedNurses((prevUnassigned) => {
      let nextUnassigned = prevUnassigned;
      for (const id of removedIds) {
        const members = nursesByTeam[id] || [];
        if (members.length === 0) continue;
        nextUnassigned = [
          ...nextUnassigned,
          ...members.map((n) => ({ ...n, team_id: null })),
        ];
      }
      return nextUnassigned;
    });

    setNursesByTeam((prevMap) => {
      const copy = { ...prevMap };
      for (const id of removedIds) {
        delete copy[id];
      }
      for (const team of next) {
        if (copy[team.team_id] === undefined) {
          copy[team.team_id] = [];
        }
      }
      return copy;
    });

    setDeletedTeamIds((prevDeleted) => {
      let out = prevDeleted;
      for (const id of removedIds) {
        if (id > 0 && !out.includes(id)) out = [...out, id];
      }
      return out;
    });

    setLocalTeams(next);
    setRightSelectedTeamId((prev) => {
      if (typeof prev === "number" && removedIds.includes(prev)) {
        return next[0]?.team_id ?? null;
      }
      return prev;
    });
    setLeftSelectedTeamId((prev) => {
      if (typeof prev === "number" && removedIds.includes(prev)) {
        return "unassigned";
      }
      return prev;
    });

    setIsDirty(true);
    closeShiftConfigModal();
  }, [
    closeShiftConfigModal,
    constraintLetters,
    localTeams,
    nursesByTeam,
    teamShiftDraftSettings,
    teamShiftDraftTeams,
    teamShiftModalCoverageValidation.messages,
  ]);

  const memberCountForManageModal = useMemo(() => {
    const map: Record<number, number> = {};
    const ids = new Set<number>();
    localTeams.forEach((t) => ids.add(t.team_id));
    manageDraftTeams?.forEach((t) => ids.add(t.team_id));
    ids.forEach((id) => {
      map[id] = nursesByTeam[id]?.length ?? 0;
    });
    return map;
  }, [localTeams, manageDraftTeams, nursesByTeam]);

  const memberCountForShiftModal = useMemo(() => {
    const map: Record<number, number> = {};
    const ids = new Set<number>();
    localTeams.forEach((team) => ids.add(team.team_id));
    teamShiftDraftTeams?.forEach((team) => ids.add(team.team_id));
    ids.forEach((id) => {
      map[id] = nursesByTeam[id]?.length ?? 0;
    });
    return map;
  }, [localTeams, nursesByTeam, teamShiftDraftTeams]);

  const totalStaffCount = useMemo(
    () =>
      Object.values(nursesByTeam).reduce((sum, list) => sum + list.length, 0) +
      unassignedNurses.length,
    [nursesByTeam, unassignedNurses.length]
  );

  const teamConfiguredTotal = useMemo(
    () =>
      teamShiftModalTeams.reduce((sum, team) => {
        const row = teamShiftModalSettings[team.team_id] ?? {};
        return (
          sum +
          constraintLetters.reduce(
            (rowSum, key) => rowSum + (Number(row[key]) || 0),
            0
          )
        );
      }, 0),
    [constraintLetters, teamShiftModalSettings, teamShiftModalTeams]
  );

  const isInvalidTeamForShiftModal = useCallback(
    (teamId: number) => {
      return Boolean(
        teamShiftModalCoverageValidation.byBucket[teamId]?.hasError
      );
    },
    [teamShiftModalCoverageValidation.byBucket]
  );

  const getInvalidTeamTooltip = useCallback(
    (teamId: number | "unassigned"): string | undefined => {
      if (teamId === "unassigned") return undefined;
      const result = currentTeamCoverageValidation.byBucket[teamId];
      if (!result?.hasError) return undefined;
      return `근무 가능 인원 부족\n${result.messages.join("\n")}`;
    },
    [currentTeamCoverageValidation.byBucket]
  );

  return (
    <>
      <CustomModal.Confirm
        isOpen={isSaveConfirmOpen}
        onClose={() => setSaveConfirmOpen(false)}
        onConfirm={triggerSaveDirect}
        isPending={isPending}
        pendingMsg={MSG.TEAM_SAVE}
        title="Team 설정을 저장하시겠습니까?"
      />
      <TeamShiftConfigModal
        isOpen={isShiftConfigOpen}
        onClose={closeShiftConfigModal}
        onConfirm={confirmShiftConfigModal}
        teams={teamShiftModalTeams}
        memberCountByTeam={memberCountForShiftModal}
        shiftSettings={teamShiftModalSettings}
        shiftDisplayInfo={shiftDisplayInfo}
        totalStaffCount={totalStaffCount}
        teamConfiguredTotal={teamConfiguredTotal}
        unassignedCount={unassignedNurses.length}
        isInvalidTeam={isInvalidTeamForShiftModal}
        onMinStaffChange={handleTeamShiftMinStaffChange}
        onRenameTeam={handleTeamShiftDraftRename}
        onDeleteTeam={handleTeamShiftDraftDelete}
        onAddTeam={handleTeamShiftDraftAdd}
      />

      <TeamManagementModal
        isOpen={isManageOpen}
        onClose={closeManageModal}
        onSave={confirmManageModal}
        teams={manageDraftTeams ?? []}
        memberCountByTeam={memberCountForManageModal}
        onAddTeam={handleManageDraftAdd}
        onRenameTeam={handleManageDraftRename}
        onDeleteTeam={handleManageDraftDelete}
      />
      <GridModalTitle title="Team 설정" />

      <div className="flex flex-col w-full min-w-0 px-10">
        {" "}
        <GridModalHeader>
          <p>
            *미지정이 있는 Team 이름을 클릭하면 미지정 인원을 확인할 수
            있습니다.
          </p>

          <div className="flex justify-end min-w-0 gap-2">
            <ActionButton
              icon={
                <img
                  src={images.icon_grade_setting_blue}
                  alt="shift setting"
                  className="w-4 h-4 transition-all mt-[1px] group-hover:[filter:brightness(0)_saturate(100%)_invert(27%)_sepia(98%)_saturate(2453%)_hue-rotate(200deg)_brightness(96%)_contrast(101%)]"
                />
              }
              onClick={openShiftConfigModal}
            >
              Shift 인원 설정/관리
            </ActionButton>
            {/* <ActionButton
              icon={
                <>
                  <img
                    src={images.icon_grade_setting_blue}
                    alt="grade setting"
                    className={`w-4 h-4 transition-all mt-[1px] group-hover:[filter:brightness(0)_saturate(100%)_invert(27%)_sepia(98%)_saturate(2453%)_hue-rotate(200deg)_brightness(96%)_contrast(101%)]`}
                  />
                </>
              }
              onClick={openManageModal}
            >
              관리
            </ActionButton> */}
          </div>
        </GridModalHeader>
        <div className="mb-5 max-w-[1500px]">
          <TeamTransferTables
            leftTableData={unassignedNurses}
            rightTableData={
              rightSelectedTeamId === "unassigned"
                ? unassignedNurses
                : typeof rightSelectedTeamId === "number"
                  ? nursesByTeam[rightSelectedTeamId] || []
                  : []
            }
            leftTableTitle="미지정"
            rightTableTitle={
              rightSelectedTeamId === "unassigned"
                ? "미지정"
                : typeof rightSelectedTeamId === "number"
                  ? localTeams.find((t) => t.team_id === rightSelectedTeamId)
                      ?.team_name || "팀 선택"
                  : "팀 선택"
            }
            selectedGroupId={groupId}
            activeNurseIdForSideProfile={highlightedNurseId}
            onNurseClick={(nurseId) => {
              setHighlightedNurseId((prev) =>
                prev === nurseId ? null : nurseId
              );
            }}
            teams={localTeams}
            nursesByTeam={nursesByTeam}
            unassignedNurses={unassignedNurses}
            invalidTeamTooltip={getInvalidTeamTooltip}
            selectedLeftTeamId={leftSelectedTeamId}
            selectedRightTeamId={rightSelectedTeamId}
            onLeftTeamChange={setLeftSelectedTeamId}
            onRightTeamChange={setRightSelectedTeamId}
            onMoveNurses={(nurseIds, fromTeamId, toTeamId) => {
              const nursesToMove: NurseWithRelation[] = [];

              if (fromTeamId === "unassigned") {
                nursesToMove.push(
                  ...unassignedNurses.filter((n) =>
                    nurseIds.includes(n.nurse_id)
                  )
                );
              } else if (fromTeamId !== null) {
                nursesToMove.push(
                  ...(nursesByTeam[fromTeamId] || []).filter((n) =>
                    nurseIds.includes(n.nurse_id)
                  )
                );
              }

              if (nursesToMove.length === 0) return;

              if (fromTeamId === "unassigned") {
                setUnassignedNurses((prev) =>
                  prev.filter((n) => !nurseIds.includes(n.nurse_id))
                );
              } else if (fromTeamId !== null) {
                setNursesByTeam((prev) => ({
                  ...prev,
                  [fromTeamId]: (prev[fromTeamId] || []).filter(
                    (n) => !nurseIds.includes(n.nurse_id)
                  ),
                }));
              }

              if (toTeamId === "unassigned") {
                setUnassignedNurses((prev) => [
                  ...prev,
                  ...nursesToMove.map((n) => ({ ...n, team_id: null })),
                ]);
              } else if (toTeamId !== null) {
                setNursesByTeam((prev) => ({
                  ...prev,
                  [toTeamId]: [
                    ...(prev[toTeamId] || []),
                    ...nursesToMove.map((n) => ({ ...n, team_id: toTeamId })),
                  ],
                }));
              }
              setIsDirty(true);
            }}
          />
        </div>
      </div>
    </>
  );
}

export default forwardRef(TeamSettingContents);
